/* FacilityCommunity.jsx – « Communauté » on the owner's facility page
   (ShowFacility.jsx): the YummiTracker lists the place is in, the photos
   taken there and the comments under those lists (backend
   routes/facilityCommunity.ts). The owner answers a photo (« Réponse de
   l'établissement », shown under it in the app) and downloads a share kit:
   a card for Instagram and a window sticker with the QR of the most followed
   list (shareKit.js). An open « Défi du mois » the place is in shows its rank. */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Grid, Link, Paper, Stack, TextField, Typography,
} from '@mui/material';
import { QRCodeCanvas } from 'qrcode.react';
import { useTranslation } from 'react-i18next';
import { community, replyToPhoto, deleteReply } from '../../utils/communityApi';
import { photoUrl } from '../../utils/sharedListsApi';
import { drawCard, drawSticker, downloadCanvas, loadImage } from './shareKit';

const C = {
  navy: '#1C2B4A', cream: '#F4EEE3', gold: '#C9A227', burgundy: '#9E3B33', border: '#EADFCB', green: '#2F7D4F', greenBg: '#E6F2EA',
};
const listUrl = (code) => `https://qrproofs.com/l/${code}`;

function Kpi({ value, label, color }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, borderColor: C.border, height: '100%' }}>
      <Typography sx={{ fontSize: 26, fontWeight: 800, color: color || C.navy, lineHeight: 1.2 }}>{value}</Typography>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
    </Paper>
  );
}

const Visited = () => {
  const { t } = useTranslation();
  return <Chip size="small" label={`✓ ${t('community.ateHere')}`} sx={{ bgcolor: C.greenBg, color: C.green, fontWeight: 700, height: 22 }} />;
};

/** A photo of the place, with the owner's reply (written, edited, removed here). */
function PhotoItem({ photo, facilityId, token, onChange }) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(!photo.reply);
  const [text, setText] = useState(photo.reply?.body ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const save = async () => {
    setBusy(true); setError('');
    try {
      const p = await replyToPhoto(facilityId, photo.id, text, token);
      onChange({ ...photo, reply: p.reply });
      setEditing(false);
    } catch (e) {
      setError(t(`community.errors.${e.message}`, { defaultValue: t('community.errors.default') }));
    } finally { setBusy(false); }
  };
  const remove = async () => {
    setBusy(true); setError('');
    try {
      await deleteReply(facilityId, photo.id, token);
      onChange({ ...photo, reply: null });
      setText(''); setEditing(true);
    } catch { setError(t('community.errors.default')); } finally { setBusy(false); }
  };

  return (
    <Box data-testid={`photo-${photo.id}`} sx={{ py: 2, '& + &': { borderTop: `1px solid ${C.border}` } }}>
      <Stack direction="row" spacing={2} alignItems="flex-start">
        <Link href={photoUrl(photo.id)} target="_blank" rel="noopener noreferrer" sx={{ flex: '0 0 auto' }}>
          <Box component="img" src={photoUrl(photo.id, true)} loading="lazy" alt={t('community.photoAlt', { name: photo.author.name })}
            sx={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 2, display: 'block' }} />
        </Link>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography sx={{ fontWeight: 700 }}>{photo.author.name}</Typography>
            {photo.visited && <Visited />}
            <Typography variant="body2" color="text.secondary">· {photo.list.name}</Typography>
          </Stack>
          <Typography variant="body2" sx={{ color: C.burgundy, mt: 0.5 }}>♥ {photo.likes}</Typography>
        </Box>
      </Stack>

      <Box sx={{ ml: { xs: 0, sm: 11 }, mt: 1.5 }}>
        {photo.reply && !editing ? (
          <Box sx={{ bgcolor: C.cream, borderLeft: `3px solid ${C.gold}`, borderRadius: 2, p: 1.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 800, color: C.navy }}>{t('community.replyLabel')}</Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{photo.reply.body}</Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <Button size="small" onClick={() => { setText(photo.reply.body); setEditing(true); }}>{t('community.edit')}</Button>
              <Button size="small" color="error" disabled={busy} onClick={remove}>{t('community.remove')}</Button>
            </Stack>
          </Box>
        ) : (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'flex-start' }}>
            <TextField
              size="small" fullWidth multiline maxRows={4} value={text} onChange={(e) => setText(e.target.value)}
              placeholder={t('community.replyPlaceholder', { name: photo.author.name })}
              inputProps={{ maxLength: 500, 'aria-label': t('community.replyAria', { name: photo.author.name }) }}
            />
            <Button variant="contained" disabled={busy || !text.trim()} onClick={save}
              sx={{ bgcolor: C.burgundy, '&:hover': { bgcolor: '#7A2E28' }, whiteSpace: 'nowrap', minHeight: 40 }}>
              {t('community.reply')}
            </Button>
          </Stack>
        )}
        {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
      </Box>
    </Box>
  );
}

export default function FacilityCommunity({ facilityId, token }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const qrRef = useRef(null);

  const load = useCallback(async () => {
    if (!facilityId || !token) return;
    try { setData(await community(facilityId, token)); } catch { setError(t('community.loadError')); }
  }, [facilityId, token, t]);
  useEffect(() => { load(); }, [load]);

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!data) return <CircularProgress size={24} />;

  const { totals, lists = [], photos = [], comments = [], challenges = [], facility } = data;
  const top = lists[0];
  const setPhoto = (p) => setData((d) => ({ ...d, photos: d.photos.map((x) => (x.id === p.id ? p : x)) }));

  const downloadCard = async () => {
    const best = [...photos].sort((a, b) => b.likes - a.likes)[0];
    const img = best ? await loadImage(photoUrl(best.id)) : null;
    const canvas = document.createElement('canvas');
    drawCard(canvas, {
      facilityName: facility.name,
      photo: img,
      t: {
        headline: t('community.cardHeadline', { count: totals.lists }),
        sub: top ? t('community.cardSub', { list: top.name, author: top.author.name }) : '',
        verified: totals.verifiedAuthors ? t('community.cardVerified', { count: totals.verifiedAuthors }) : '',
        footer: t('community.cardFooter'),
      },
    });
    downloadCanvas(canvas, `yummitracker-${facility.id}.png`);
  };
  const downloadSticker = () => {
    const canvas = document.createElement('canvas');
    drawSticker(canvas, {
      facilityName: facility.name,
      qr: qrRef.current,
      t: { title: t('community.stickerTitle'), sub: t('community.stickerSub', { list: top.name }), scan: t('community.stickerScan') },
    });
    downloadCanvas(canvas, `yummitracker-sticker-${facility.id}.png`);
  };

  if (!lists.length) {
    return (
      <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, borderColor: C.border, bgcolor: C.cream }}>
        <Typography sx={{ fontWeight: 800, color: C.navy }}>{t('community.emptyTitle')}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{t('community.emptyText')}</Typography>
      </Paper>
    );
  }

  return (
    <Box>
      <Stack direction="row" spacing={1.5} alignItems="baseline" flexWrap="wrap" useFlexGap>
        <Typography variant="h5" sx={{ fontWeight: 800, color: C.navy }}>{t('community.inLists', { count: totals.lists })}</Typography>
        {totals.newLists > 0 && (
          <Chip size="small" label={t('community.newThisWeek', { count: totals.newLists })} sx={{ bgcolor: C.greenBg, color: C.green, fontWeight: 700 }} />
        )}
      </Stack>

      <Grid container spacing={1.5} sx={{ mt: 1 }}>
        <Grid size={{ xs: 6, md: 3 }}><Kpi value={totals.imports} label={t('community.kpiImports')} /></Grid>
        <Grid size={{ xs: 6, md: 3 }}><Kpi value={totals.photos} label={t('community.kpiPhotos')} /></Grid>
        <Grid size={{ xs: 6, md: 3 }}><Kpi value={totals.comments} label={t('community.kpiComments')} /></Grid>
        <Grid size={{ xs: 6, md: 3 }}><Kpi value={`✓ ${totals.verifiedAuthors}`} label={t('community.kpiVerified')} color={C.green} /></Grid>
      </Grid>

      <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
        {lists.map((l) => (
          <Chip key={l.id} component="a" clickable href={listUrl(l.shareCode)} target="_blank" rel="noopener noreferrer"
            label={`${l.name} · ${l.author.name}`} variant="outlined" sx={{ borderColor: C.border, fontWeight: 600 }} />
        ))}
      </Stack>

      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid size={{ xs: 12, md: 7 }}>
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, borderColor: C.border }}>
            <Typography sx={{ fontWeight: 800, color: C.navy }}>{t('community.photosTitle')}</Typography>
            {photos.length === 0 && <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{t('community.noPhotos')}</Typography>}
            {photos.map((p) => <PhotoItem key={p.id} photo={p} facilityId={facility.id} token={token} onChange={setPhoto} />)}

            {comments.length > 0 && (
              <>
                <Typography sx={{ fontWeight: 800, color: C.navy, mt: 3 }}>{t('community.commentsTitle')}</Typography>
                {comments.map((c) => (
                  <Box key={c.id} sx={{ py: 1.5, '& + &': { borderTop: `1px solid ${C.border}` } }}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Typography sx={{ fontWeight: 700 }}>{c.author.name}</Typography>
                      {c.visitedPlaces > 0 && <Visited />}
                      <Typography variant="body2" color="text.secondary">· {c.list.name}</Typography>
                    </Stack>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>{c.body}</Typography>
                  </Box>
                ))}
              </>
            )}
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 5 }}>
          <Paper sx={{ p: 2.5, borderRadius: 3, bgcolor: C.navy, color: C.cream }}>
            <Typography sx={{ fontWeight: 800 }}>{t('community.kitTitle')}</Typography>
            <Typography variant="body2" sx={{ color: '#D9C3A0', mt: 1 }}>{t('community.kitText')}</Typography>
            <Stack direction={{ xs: 'column', sm: 'row', md: 'column', lg: 'row' }} spacing={1} sx={{ mt: 2 }}>
              <Button variant="contained" onClick={downloadCard}
                sx={{ bgcolor: C.gold, color: C.navy, fontWeight: 800, '&:hover': { bgcolor: '#B18E1F' } }}>
                {t('community.downloadCard')}
              </Button>
              <Button variant="outlined" onClick={downloadSticker} sx={{ color: C.cream, borderColor: C.cream }}>
                {t('community.downloadSticker')}
              </Button>
            </Stack>
            {/* Drawn into the sticker only. */}
            <Box sx={{ display: 'none' }}>
              <QRCodeCanvas ref={qrRef} value={listUrl(top.shareCode)} size={400} level="M" />
            </Box>
          </Paper>
          {challenges.map((c) => (
            <Paper key={c.id} variant="outlined" sx={{ p: 2.5, mt: 2, borderRadius: 3, borderColor: C.border }} data-testid={`challenge-${c.id}`}>
              <Typography sx={{ fontWeight: 800, color: C.navy }}>{t('community.challengeTitle')}</Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                {t('community.challengeText', { title: c.title, rank: c.rank, count: c.votes })}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, fontWeight: 700, color: C.burgundy }}>{t('community.challengeInvite')}</Typography>
            </Paper>
          ))}
        </Grid>
      </Grid>
    </Box>
  );
}
